import type { MouseEvent, ReactNode } from "react";
import { Link, useNavigate } from "react-router";

import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";

export type VoltColumn<T> = {
  key: string;
  header: ReactNode;
  cell: (row: T) => ReactNode;
  align?: "left" | "right";
  hideOnMobile?: boolean;
  className?: string;
};

// Clicks on buttons, links or inputs inside a row keep their own action.
const INTERACTIVE = "a, button, input, select, textarea, [role=button]";

const alignClass = (align?: "left" | "right") =>
  align === "right" ? "text-right tabular-nums" : undefined;

export const VoltDataTable = <T,>({
  rows,
  columns,
  rowKey,
  rowHref,
  loading = false,
  empty,
  className,
}: {
  rows: T[];
  columns: VoltColumn<T>[];
  rowKey: (row: T) => string | number;
  rowHref?: (row: T) => string;
  loading?: boolean;
  empty?: ReactNode;
  className?: string;
}) => {
  const isMobile = useIsMobile();
  const navigate = useNavigate();

  if (loading) {
    return (
      <div className={cn("flex flex-col gap-2", className)} aria-busy>
        {[0, 1, 2].map((i) => (
          <Skeleton key={i} className="h-10 w-full" />
        ))}
      </div>
    );
  }
  if (rows.length === 0) return <>{empty ?? null}</>;

  // The row stays a plain row for screen readers; the first cell carries a
  // real link for keyboard users, the row click is a mouse shortcut.
  const clickProps = (row: T) =>
    rowHref
      ? {
          onClick: (e: MouseEvent) => {
            if ((e.target as Element).closest(INTERACTIVE)) return;
            navigate(rowHref(row));
          },
        }
      : {};
  const firstCell = (row: T, content: ReactNode) =>
    rowHref ? (
      <Link to={rowHref(row)} className="hover:underline">
        {content}
      </Link>
    ) : (
      content
    );

  if (isMobile) {
    const [first, ...rest] = columns.filter((c) => !c.hideOnMobile);
    return (
      <ul className={cn("flex flex-col gap-2", className)}>
        {rows.map((row) => (
          <li
            key={rowKey(row)}
            className={cn(
              "rounded-lg border bg-card p-3 text-sm",
              rowHref && "cursor-pointer active:bg-accent",
            )}
            {...clickProps(row)}
          >
            {first ? (
              <div className="font-medium">
                {firstCell(row, first.cell(row))}
              </div>
            ) : null}
            {rest.length > 0 ? (
              <dl className="mt-2 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1">
                {rest.map((c) => (
                  <div key={c.key} className="contents">
                    <dt className="text-muted-foreground">{c.header}</dt>
                    <dd className={cn("min-w-0", alignClass(c.align))}>
                      {c.cell(row)}
                    </dd>
                  </div>
                ))}
              </dl>
            ) : null}
          </li>
        ))}
      </ul>
    );
  }

  return (
    <Table className={className}>
      <TableHeader>
        <TableRow>
          {columns.map((c) => (
            <TableHead
              key={c.key}
              className={cn(alignClass(c.align), c.className)}
            >
              {c.header}
            </TableHead>
          ))}
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((row) => (
          <TableRow
            key={rowKey(row)}
            className={cn(rowHref && "cursor-pointer")}
            {...clickProps(row)}
          >
            {columns.map((c, i) => (
              <TableCell
                key={c.key}
                className={cn(alignClass(c.align), c.className)}
              >
                {i === 0 ? firstCell(row, c.cell(row)) : c.cell(row)}
              </TableCell>
            ))}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};
